import { FC, useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  CalendarFold,
  Ellipsis,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { useOrgStore } from '@/stores/org-store';
import { useNotification } from '@/components/notifications';
import * as Sentry from '@sentry/react';
import {
  deleteSchedule,
  listSchedules,
  pauseSchedule,
  resumeSchedule,
  type WorkflowSchedule,
} from '@/services/workflow/schedule';
import { cn } from '@/lib/utils';
import { scheduleCadenceLabel } from './schedule-helpers';
import { isOneShot } from '@/services/workflow/schedule';
import { ScheduleFormModal } from '@/pages/schedules/components/schedule-form-modal';

function PulseDot({
  enabled,
  nextRunAt,
}: {
  enabled: boolean;
  nextRunAt: string | null;
}) {
  const isError =
    enabled && nextRunAt && new Date(nextRunAt).getTime() < Date.now();
  const status = !enabled ? 'paused' : isError ? 'error' : 'active';

  return (
    <span
      className={cn(
        'inline-block size-[7px] shrink-0 rounded-full',
        status === 'active' && 'bg-emerald-500',
        status === 'paused' && 'bg-amber-400',
        status === 'error' && 'bg-red-500',
        status === 'active' && 'animate-pulse',
      )}
    />
  );
}

interface ScheduleConfigSectionProps {
  workflowId: string;
  workflowName?: string;
  isVisible: boolean;
  // 'section' renders inside the workflow-editor sidebar; 'modal' drops the
  // outer chrome so it sits under the shared Modal's own header.
  variant?: 'section' | 'modal';
  onChanged?: () => void;
}

export const ScheduleConfigSection: FC<ScheduleConfigSectionProps> = ({
  workflowId,
  workflowName,
  isVisible,
  variant = 'section',
  onChanged,
}) => {
  const { currentOrg } = useOrgStore();
  const notification = useNotification();
  const orgId = currentOrg?.orgid || '';

  const [schedules, setSchedules] = useState<WorkflowSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeScheduleId, setActiveScheduleId] = useState<
    string | 'new' | null
  >(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const refreshSchedules = useCallback(async () => {
    if (!orgId || !workflowId) return;
    try {
      const result = await listSchedules(orgId, workflowId);
      setSchedules(result);
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'schedule-config-load', workflowId },
      });
      notification.error('Failed to load schedules');
    }
  }, [orgId, workflowId, notification]);

  const loadSchedules = useCallback(async () => {
    setIsLoading(true);
    try {
      await refreshSchedules();
      setActiveScheduleId(null);
    } finally {
      setIsLoading(false);
    }
  }, [refreshSchedules]);

  useEffect(() => {
    if (isVisible) {
      loadSchedules();
    }
  }, [isVisible, loadSchedules]);

  const handleScheduleChanged = async () => {
    await loadSchedules();
    onChanged?.();
  };

  const existingSchedule =
    activeScheduleId && activeScheduleId !== 'new'
      ? (schedules.find((s) => s.id === activeScheduleId) ?? null)
      : null;

  // The confirmation modal lives outside the row loop, so it needs its own
  // lookup to know whether it is cancelling a one-shot or deleting a cron.
  const deleteTargetIsOneShot = deleteTargetId
    ? isOneShot(schedules.find((s) => s.id === deleteTargetId) ?? {})
    : false;

  const scheduleCountLabel =
    schedules.length === 1 ? '1 schedule' : `${schedules.length} schedules`;
  let headerLabel = 'Schedule';
  if (variant === 'modal') {
    headerLabel = schedules.length > 0 ? scheduleCountLabel : '';
  }

  return (
    <div className={cn(variant === 'section' && 'p-4 border-b')}>
      <div className='flex items-center justify-between'>
        <span
          className={cn(
            'text-sm font-medium',
            variant === 'modal' && 'text-muted-foreground',
          )}
        >
          {headerLabel}
        </span>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => setActiveScheduleId('new')}
        >
          Create Schedule
          <Plus className='size-4' />
        </Button>
      </div>

      {isLoading ? (
        <div className='mt-2 flex flex-col gap-2'>
          {[0, 1].map((i) => (
            <div key={i} className='flex items-center gap-2 p-3'>
              <Skeleton className='size-[7px] shrink-0 rounded-full' />
              <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                <Skeleton className='h-4 w-1/2' />
                <Skeleton className='h-3 w-3/4' />
              </div>
              <Skeleton className='size-9 shrink-0 rounded-md' />
            </div>
          ))}
        </div>
      ) : schedules.length === 0 ? (
        <div className='mt-2 flex items-center gap-2 rounded-md border-2 border-dashed p-3'>
          <div className='flex size-9 shrink-0 items-center justify-center rounded-md bg-accent'>
            <CalendarFold className='size-5 text-muted-foreground' />
          </div>
          <div className='min-w-0'>
            <p className='text-sm font-medium'>No schedules yet</p>
            <p className='text-sm text-muted-foreground'>
              Run this workflow on a schedule
            </p>
          </div>
        </div>
      ) : (
        <div className='mt-1 flex flex-col gap-1'>
          {schedules.map((s) => {
            // Not parseCronToMode(s.cron_expression) directly: that field is
            // null on a one-shot and this call site has no try/catch, so it
            // would throw rather than degrade.
            const human = scheduleCadenceLabel(s);
            const oneShot = isOneShot(s);
            const rowClass =
              'flex min-w-0 flex-1 flex-col items-start gap-1 text-left';
            const rowBody = (
              <>
                <span className='truncate text-sm font-medium'>
                  {s.name || human}
                </span>
                <span className='truncate text-sm text-muted-foreground'>
                  {human}
                </span>
              </>
            );

            return (
              <div
                key={s.id}
                className='flex items-center gap-2 rounded-md bg-accent p-3 shadow-sm'
              >
                <PulseDot enabled={s.enabled} nextRunAt={s.next_run_at} />
                {/* Hiding the Edit menu item is not enough on its own: the
                    row itself opened the same edit modal, which would fill
                    in cron defaults for a one-shot and then save against an
                    API that rejects one-shot updates.

                    Plain text rather than a disabled button: a one-shot has
                    nothing to open, so it is not a control, and the browser's
                    disabled styling greyed the name and cadence out -- a
                    pending run then read as cancelled while the PulseDot
                    beside it still showed live. */}
                {oneShot ? (
                  <div className={rowClass}>{rowBody}</div>
                ) : (
                  <button
                    type='button'
                    className={rowClass}
                    onClick={() => setActiveScheduleId(s.id)}
                  >
                    {rowBody}
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='size-9 shrink-0'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Ellipsis className='size-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    {/* A one-shot has no recurrence to edit, pause, or
                        resume, and the API rejects all three. */}
                    {!oneShot && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveScheduleId(s.id);
                        }}
                      >
                        <Pencil className='size-4 mr-2' />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {!oneShot && (
                      <DropdownMenuItem
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const updated = s.enabled
                              ? await pauseSchedule(orgId, workflowId, s.id)
                              : await resumeSchedule(orgId, workflowId, s.id);
                            if (updated) {
                              notification.success(
                                updated.enabled
                                  ? 'Schedule resumed'
                                  : 'Schedule paused',
                              );
                              await refreshSchedules();
                              onChanged?.();
                            }
                          } catch (err) {
                            Sentry.captureException(err, {
                              extra: {
                                context: 'schedule-config-toggle',
                                workflowId,
                              },
                            });
                            notification.error('Failed to toggle schedule');
                          }
                        }}
                      >
                        {s.enabled ? (
                          <>
                            <Pause className='size-4 mr-2' />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className='size-4 mr-2' />
                            Resume
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                    {!oneShot && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTargetId(s.id);
                      }}
                    >
                      <Trash2 className='size-4 mr-2' />
                      {oneShot ? 'Cancel' : 'Delete'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      <ScheduleFormModal
        open={activeScheduleId !== null}
        onOpenChange={(open) => {
          if (!open) setActiveScheduleId(null);
        }}
        schedule={
          existingSchedule
            ? {
                ...existingSchedule,
                workflow_name: workflowName ?? '',
                last_run_at: null,
              }
            : null
        }
        lockedWorkflowId={workflowId}
        lockedWorkflowName={workflowName}
        onSaved={handleScheduleChanged}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteTargetId}
        title={
          deleteTargetIsOneShot ? 'Cancel Scheduled Run' : 'Delete Schedule'
        }
        description={
          deleteTargetIsOneShot
            ? 'Are you sure you want to cancel this scheduled run? It will never fire. This action cannot be undone.'
            : 'Are you sure you want to delete this schedule? Scheduled runs will stop immediately. This action cannot be undone.'
        }
        buttonLabel={deleteTargetIsOneShot ? 'Cancel Run' : 'Delete Schedule'}
        isLoading={isDeleting}
        handleDelete={async () => {
          if (!deleteTargetId) return;
          setIsDeleting(true);
          try {
            await deleteSchedule(orgId, workflowId, deleteTargetId);
            notification.success(
              deleteTargetIsOneShot
                ? 'Scheduled run cancelled'
                : 'Schedule deleted',
            );
            await refreshSchedules();
            onChanged?.();
          } catch (err) {
            Sentry.captureException(err, {
              extra: { context: 'schedule-config-delete', workflowId },
            });
            notification.error(
              deleteTargetIsOneShot
                ? 'Failed to cancel scheduled run'
                : 'Failed to delete schedule',
            );
          } finally {
            setIsDeleting(false);
            setDeleteTargetId(null);
          }
        }}
        handleCancel={() => setDeleteTargetId(null)}
      />
    </div>
  );
};
